import { useState } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ExperimentsManager({ experiments, onRefresh, headers }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [formData, setFormData] = useState({ title: '', description: '', tech_stack: [], link: '', image_url: '', published: false });
    const [techInput, setTechInput] = useState('');

    const handleSave = async () => {
        try {
            if (editItem) {
                await axios.put(`${API}/experiments/${editItem.id}`, formData, { headers });
                toast.success('Experiment updated!');
            } else {
                await axios.post(`${API}/experiments`, formData, { headers });
                toast.success('Experiment created!');
            }
            setDialogOpen(false);
            setEditItem(null);
            setFormData({ title: '', description: '', tech_stack: [], link: '', image_url: '', published: false });
            onRefresh();
        } catch (error) {
            toast.error('Failed to save');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this experiment?')) return;
        try {
            await axios.delete(`${API}/experiments/${id}`, { headers });
            toast.success('Experiment deleted!');
            onRefresh();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const openEdit = (item) => {
        setEditItem(item);
        setFormData({ title: item.title, description: item.description, tech_stack: item.tech_stack || [], link: item.link, image_url: item.image_url, published: item.published });
        setDialogOpen(true);
    };

    const addTech = () => {
        if (techInput.trim()) {
            setFormData({...formData, tech_stack: [...formData.tech_stack, techInput.trim()]});
            setTechInput('');
        }
    };

    const removeTech = (index) => {
        setFormData({...formData, tech_stack: formData.tech_stack.filter((_, i) => i !== index)});
    };

    return (
        <div className="retro-card p-6" data-testid="experiments-manager">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg uppercase tracking-tight" style={{ fontFamily: "'Silkscreen', cursive" }}>
                    Manage Experiments
                </h2>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="rounded-none border-2 border-primary bg-primary text-primary-foreground retro-btn text-xs uppercase tracking-widest" data-testid="add-experiment-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Add Experiment
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-none border-2 max-w-2xl">
                        <DialogHeader>
                            <DialogTitle className="uppercase tracking-tight" style={{ fontFamily: "'Silkscreen', cursive" }}>
                                {editItem ? 'Edit Experiment' : 'New Experiment'}
                            </DialogTitle>
                            <DialogDescription>Fill in the details below</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-xs uppercase tracking-widest">Title</Label>
                                <Input className="rounded-none border-2 mt-1" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} data-testid="experiment-title-input" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest">Description</Label>
                                <Textarea className="rounded-none border-2 mt-1" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} data-testid="experiment-description-input" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest">Tech Stack</Label>
                                <div className="flex gap-2 mt-1">
                                    <Input className="rounded-none border-2" value={techInput} onChange={(e) => setTechInput(e.target.value)} placeholder="Add technology" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTech())} />
                                    <Button type="button" variant="outline" onClick={addTech} className="rounded-none border-2">Add</Button>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {formData.tech_stack.map((tech, i) => (
                                        <span key={i} className="px-2 py-1 bg-muted text-xs flex items-center gap-1">
                                            {tech}
                                            <X className="h-3 w-3 cursor-pointer" onClick={() => removeTech(i)} />
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <Label className="text-xs uppercase tracking-widest">Link</Label>
                                    <Input className="rounded-none border-2 mt-1" value={formData.link} onChange={(e) => setFormData({...formData, link: e.target.value})} />
                                </div>
                                <div className="flex-1">
                                    <Label className="text-xs uppercase tracking-widest">Image URL</Label>
                                    <Input className="rounded-none border-2 mt-1" value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} />
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch checked={formData.published} onCheckedChange={(checked) => setFormData({...formData, published: checked})} data-testid="experiment-published-switch" />
                                <Label className="text-xs uppercase tracking-widest">Published</Label>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => { setDialogOpen(false); setEditItem(null); }} className="rounded-none border-2">Cancel</Button>
                            <Button onClick={handleSave} className="rounded-none border-2 border-primary bg-primary text-primary-foreground" data-testid="save-experiment-btn">Save</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {experiments.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No experiments yet. Start experimenting!</p>
            ) : (
                <div className="space-y-4">
                    {experiments.map(item => (
                        <div key={item.id} className="flex items-center justify-between p-4 border-2 border-border" data-testid={`experiment-row-${item.id}`}>
                            <div className="flex-1">
                                <h3 className="font-bold">{item.title}</h3>
                                <p className="text-sm text-muted-foreground">{item.description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                {item.published ? (
                                    <span className="text-xs uppercase tracking-widest text-green-500 flex items-center gap-1"><Check className="h-3 w-3" /> Live</span>
                                ) : (
                                    <span className="text-xs uppercase tracking-widest text-muted-foreground">Draft</span>
                                )}
                                <Button variant="outline" size="icon" onClick={() => openEdit(item)} className="rounded-none border-2" data-testid={`edit-experiment-${item.id}`}><Edit className="h-4 w-4" /></Button>
                                <Button variant="outline" size="icon" onClick={() => handleDelete(item.id)} className="rounded-none border-2 text-destructive" data-testid={`delete-experiment-${item.id}`}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
